/* eslint-disable react/no-array-index-key */
import React, {
  Component,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  message,
  Select,
  Form,
  Input,
  Alert,
} from 'antd';

import { updateReferral, getReferralDocumenStatus } from '../../actions/records';
import { getReferredStatuses } from '../../actions/userProfile';
import { captureAnalytics } from '../../actions/preload';
import { verifyFeatureAccess, verifyReferralMgmtAccess } from '../../global/featureFlags';
import { buildSmsTemplateMessage } from '../../util/smsTemplateVariables';

import ModalWrapper from './ModalWrapper';
import { openModal } from '../../actions/modal';
import { ReferralStatusLabel } from '../../containers/records/shared/referralStatusPresentation';
import {
  ReferralStatusConsequenceBadges,
  ReferralStatusConsequencePanel,
  buildReferralStatusSuccessMessage,
} from '../../containers/records/shared/referralStatusConsequences';

export class UpdateReferral extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendToken: this.props.modalType.data.sendToken,
      parentType: this.props.modalType.data.parentType,
      fileToken: this.props.modalType.data.fileToken,
      referralStatuses: [],
      referralStatusId: 1,
      referralStatus: null,
      referralNote: this.props.modalType.data.referralNote,
      smsMessageFeature: false,
      mairaCallFeature: false,
      mairaAutoCallFeature: false,
      initialDueDate: null,
      keepDueDate: false,
    };
  }

  componentDidMount = async () => {
    const { getReferredStatuses, getReferralDocumenStatus } = this.props;

    const { fileToken, sendToken, parentType } = this.props.modalType.data;

    const canUpdateReferralStatus = await verifyReferralMgmtAccess();

    const smsMessageFeature = await verifyFeatureAccess('referral_sms');
    const mairaCallFeature = await verifyFeatureAccess('referral_maira_call');
    const mairaAutoCallFeature = await verifyFeatureAccess('referral_maira_auto_call');

    const statusResponse = await getReferralDocumenStatus(fileToken, sendToken, parentType);
    await getReferredStatuses(statusResponse?.data?.consequence_context || {});

    const { allReferredStatuses, currentReferralStatus, currentReferredDocumentStatus } = this.props;

    const selectedStatus = allReferredStatuses.find((status) => status.id === currentReferralStatus);
    this.setState({
      referralStatuses: allReferredStatuses,
      referralStatus: selectedStatus,
      referralStatusId: selectedStatus?.id,
      initialDueDate: currentReferredDocumentStatus?.due_date || null,
      canUpdateReferralStatus,
      smsMessageFeature,
      mairaCallFeature,
      mairaAutoCallFeature,
    });
  };

  update = async (event) => {
    event.preventDefault();
    const {
      sendToken,
      parentType,
      referralStatus,
      referralNote,
      fileToken,
      smsMessageFeature,
      mairaCallFeature,
      mairaAutoCallFeature,
      initialDueDate,
      keepDueDate,
      referralStatusId,
    } = this.state;
    const statusChanged = referralStatusId !== this.props.currentReferralStatus;

    const updateResponse = await this.props.updateReferral(
      fileToken,
      sendToken,
      referralStatus,
      parentType,
      Boolean(statusChanged && keepDueDate && initialDueDate),
      referralNote || '',
    );
    if (updateResponse?.error) return;

    const { hideModal, openModal } = this.props;
    const { updatedStatusData } = this.props;
    const record = updatedStatusData?.find((item) => item.file_token === fileToken);
    const postUpdateStatusRow = updateResponse?.data?.referred_document_status_id
      || record?.referral_status || this.props.referredDocumentStatus;

    if (postUpdateStatusRow) {
      const currentReferralStatus = postUpdateStatusRow;
      const linkedSmsTemplate = currentReferralStatus?.linked_sms_template;
      const linkedCallTemplate = currentReferralStatus?.linked_call_template;
      const linkedReplyTemplate = currentReferralStatus?.linked_reply_template;
      const patientPhoneNumber = record?.patient_phone_number || this.props.patientPhoneNumber;
      const patientName = record?.patient_name || this.props.patientName;
      const patientDOB = record?.patient_dob || this.props.patientDOB;
      const receiverType = record?.receiver_type || this.props.receiverType;
      const referringProvider = record?.referring_provider || this.props.referringProvider;
      const shouldOpenSmsPhoneCollection = Boolean(linkedSmsTemplate && !patientPhoneNumber);
      hideModal();
      if (shouldOpenSmsPhoneCollection) {
        const userTemplates = [linkedSmsTemplate];
        const messageText = buildSmsTemplateMessage({
          templateMessage: linkedSmsTemplate.message,
          patientName,
          referringProvider,
        });
        const modalType = {
          type: 'SMS_MESSAGE',
          data: {
            sendToken,
            receiverType,
            patientName: patientName || '',
            referringProvider,
            messageTemplates: userTemplates,
            messageText: messageText,
            url: linkedSmsTemplate.short_url,
            originalUrl: linkedSmsTemplate.url,
            patientPhoneNumber,
            smsMessageFeature,
          },
        };
        openModal(modalType);

        // Notify user that auto-SMS was skipped and manual entry is needed
        message.info({
          content: 'No patient phone number on file. Enter one below to send SMS.',
          duration: 5,
        });

        // If a MAIRA call template is also linked, surface that the auto-call was deferred
        // (the call modal won't open while the SMS phone-collection modal is in front).
        if (linkedCallTemplate && mairaAutoCallFeature && mairaCallFeature) {
          message.info({
            content: 'MAIRA call deferred. Send the SMS first, then trigger the MAIRA call manually from Referral Management.',
            duration: 6,
          });
        }
      }

      if (linkedCallTemplate && mairaAutoCallFeature && mairaCallFeature && !shouldOpenSmsPhoneCollection) {
        if (!patientName || !patientDOB) {
          message.info({
            content: 'MAIRA call template is linked to this status, but patient name and date of birth are required before a call can be queued.',
            duration: 5,
          });
        } else {
          openModal({
            type: 'MAIRA_CALL',
            data: {
              sendToken,
              receiverType,
              patientName,
              patientPhoneNumber,
              referringProvider,
              callTemplates: [linkedCallTemplate],
              selectedTemplateId: linkedCallTemplate.id,
              mairaCallFeature,
            },
          });

          if (!patientPhoneNumber) {
            message.info({
              content: 'No patient phone number on file. Enter one below to queue the MAIRA call.',
              duration: 5,
            });
          }
        }
      }

      let successMessage = buildReferralStatusSuccessMessage({
        linkedSmsTemplate,
        linkedReplyTemplate,
        patientPhoneNumber,
        statusChanged,
        previousDueDate: initialDueDate,
        resultingDueDate: postUpdateStatusRow.due_date,
        dueDatePreserved: Boolean(statusChanged && keepDueDate && initialDueDate),
      });
      if (linkedCallTemplate && mairaAutoCallFeature && mairaCallFeature
        && patientName && patientDOB && !shouldOpenSmsPhoneCollection) {
        successMessage += ' Review the MAIRA call details to queue the call.';
      }

      message.success({
        content: successMessage,
      });
    }
  };

  handleChange = (value) => {
    this.setState({ referralStatus: value, referralStatusId: value.id });
  };

  handleNoteChange = (e) => {
    this.setState({ referralNote: e.target.value });
  };

  handleKeepDueDateChange = (checked) => {
    captureAnalytics('referral_due_date_retention_changed', { checked });
    this.setState({ keepDueDate: checked });
  };

  render() {
    const { isLoading, hideModal, currentReferralStatus } = this.props;
    const {
      referralStatuses,
      referralStatusId,
      referralStatus,
      referralNote,
      canUpdateReferralStatus,
      initialDueDate,
      keepDueDate,
    } = this.state;

    const disableButton = canUpdateReferralStatus;
    const customContentStyle = {
      width: '340px',
    };

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.update}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={!disableButton || isLoading}
        modalTitle="Update Referral"
        form="updateReferral"
        isDraggable
      >
        <Form
          layout="vertical"
          onFinish={this.update}
          id="updateReferral"
          initialValues={{
            additionalNote: referralNote,
          }}
        >
          {!disableButton && (
            <div style={{ marginBottom: '25px' }}>
              <Alert
                message="Your plan doesn't include access to update statuses for referred records. Please upgrade your plan to make any changes."
                type="info"
                showIcon
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Item
              label="Referral Status"
              style={{ display: 'flex' }}
            >
              <Select
                style={{ width: '400px' }}
                placeholder="Select Referral Status"
                value={referralStatusId || '__no_status__'}
                optionLabelProp="label"
                dropdownClassName="referral-status-consequence-dropdown"
                onChange={(selectedId) => {
                  if (selectedId === '__no_status__') return;
                  const selectedStatus = referralStatuses.find((status) => status.id === selectedId);
                  this.handleChange(selectedStatus);
                }}
              >
                {!referralStatusId && (
                  <Select.Option
                    key="__no_status__"
                    value="__no_status__"
                    label="No Status"
                    disabled
                  >
                    <ReferralStatusLabel
                      status="No Status"
                      statuses={referralStatuses}
                      fallbackIndex={referralStatuses.length}
                    />
                  </Select.Option>
                )}
                {referralStatuses
                  .filter((status) => status.active !== false || status.id === referralStatusId)
                  .map((status, index) => (
                    <Select.Option
                      key={status.id}
                      value={status.id}
                      label={`${status.name}${status.active === false ? ' (Deactivated)' : ''}`}
                      disabled={status.active === false}
                    >
                      <span className="referral-status-option-row">
                        <ReferralStatusLabel
                          status={status}
                          statuses={referralStatuses}
                          fallbackIndex={index}
                          inactive={status.active === false}
                        />
                        <ReferralStatusConsequenceBadges status={status} />
                      </span>
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </div>
          {referralStatus && referralStatusId !== currentReferralStatus && (
            <ReferralStatusConsequencePanel
              status={referralStatus}
              currentDueDate={initialDueDate}
              patientPhoneNumber={this.props.patientPhoneNumber}
              showKeepDueDate={Boolean(initialDueDate)}
              keepDueDate={keepDueDate}
              onKeepDueDateChange={this.handleKeepDueDateChange}
            />
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Item
              style={{ width: '400px' }}
              label="Additional Note"
              name="additionalNote"
            >
              <Input.TextArea
                placeholder="Additional Note"
                onChange={this.handleNoteChange}
              />
            </Form.Item>
          </div>
        </Form>
      </ModalWrapper>
    );
  }
}

UpdateReferral.defaultProps = {
  paramTools: {
    push: () => {},
    params: {},
    stringify: () => {},
    stringifyOptions: {},
  },
  currentReferralStatus: null,
  currentReferredDocumentStatus: null,
};

UpdateReferral.propTypes = {
  hideModal: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  allReferredStatuses: PropTypes.array.isRequired,
  getReferredStatuses: PropTypes.func.isRequired,
  updatedStatusData: PropTypes.array,
  referredDocumentStatus: PropTypes.object,
  patientName: PropTypes.string,
  patientDOB: PropTypes.string,
  patientPhoneNumber: PropTypes.string,
  receiverType: PropTypes.string,
  referringProvider: PropTypes.string,
  updateReferral: PropTypes.func.isRequired,
  getReferralDocumenStatus: PropTypes.func.isRequired,
  currentReferralStatus: PropTypes.number,
  currentReferredDocumentStatus: PropTypes.object,
  paramTools: PropTypes.object,
};

export default connect(state => ({
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  currentReferralStatus: state.records.currentReferralStatus,
  currentReferredDocumentStatus: state.records.currentReferredDocumentStatus,
  data: state.labels.data,
  allReferredStatuses: state.userProfile.allReferredStatuses,
  departmentUsers: state.userProfile.departmentUsers,
  updatedStatusData: state.records.received_data,
  patientName: state.records.patientName,
  patientDOB: state.records.patientDOB,
  patientPhoneNumber: state.records.patientPhoneNumber,
  receiverType: state.records.receiverType,
  referringProvider: state.records.referringProvider,
  referredDocumentStatus: state.records.referredDocumentStatusId,
}), {
  updateReferral,
  getReferralDocumenStatus,
  getReferredStatuses,
  openModal,
})(UpdateReferral);
